// Maps the `offer_hub_answer` tool-call args into a hub-offer view-model. The
// offer arrives on its own assistant message and only when the knowledge-hub
// (ASA) lane has an answer to offer, so its presence is the "show the offer"
// signal; `variant` picks the microcopy.

import type { Message } from '@ag-ui/client';
import { AG_UI_TOOL, AG_UI_ROLE, HUB_SOURCE, isHubSource } from '../agent/ag-ui.constants';
import type { HubSource } from '../agent/ag-ui.constants';
import { parseToolArgs } from '../agent/tool-args';
import type { AssistantMessageLike } from './doc-answer';

/** `additional` = offered on top of a local answer; `fallback` = offered instead of a missing one. */
export type HubOfferVariant = 'additional' | 'fallback';

/** View-model for a knowledge-hub (ASA) answer offer. */
export interface HubOffer {
  /** The offer tool-call id, echoed back as the accepting tool message's toolCallId. */
  offerId: string;
  variant: HubOfferVariant;
  /** The hub the offer draws from (analytics); ASA until the backend names others. */
  source: HubSource;
  /** Chunks the availability gate approved — prefetched on accept so citations are ready. */
  chunkIds: string[];
}

function toVariant(value: unknown): HubOfferVariant {
  return value === 'additional' ? 'additional' : 'fallback';
}

function toSource(value: unknown): HubSource {
  return isHubSource(value) ? value : HUB_SOURCE.ASA;
}

function toChunkIds(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

/** The newest assistant message, past any trailing tool/user messages. */
export function lastAssistantMessage(messages: readonly Message[]): Message | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role === AG_UI_ROLE.ASSISTANT) return message;
  }
  return null;
}

/**
 * Which message a hub run's answer belongs to: the newest assistant message,
 * or `null` while that's still the offer — the answer hasn't streamed yet, and
 * tagging the offer as a hub answer would render it as a spurious failure line.
 */
export function hubAnswerMessageId(messages: Message[]): string | null {
  const message = lastAssistantMessage(messages);
  if (!message || toHubOffer(message as AssistantMessageLike)) return null;
  return message.id;
}

/**
 * The run's decision on offering a hub answer, from the `knowledge_hub` run
 * state: `shown` when it offered, `hidden` for any other lane outcome, `null`
 * when the lane never reported (non-clinical run, or the caller isn't entitled).
 */
export function hubOfferResponse(state: unknown): 'shown' | 'hidden' | null {
  const outcome = (state as { knowledge_hub?: { outcome?: unknown } } | undefined)?.knowledge_hub
    ?.outcome;
  if (typeof outcome !== 'string' || outcome === 'denied') return null;
  return outcome === 'offered' ? 'shown' : 'hidden';
}

/** The trailing fallback (ASA) offer of an exchange, or `null` — none, or an `additional` one. */
export function trailingFallbackOffer(messages: readonly Message[]): HubOffer | null {
  const message = lastAssistantMessage(messages);
  const offer = message && toHubOffer(message as AssistantMessageLike);
  return offer?.variant === 'fallback' ? offer : null;
}

/** The knowledge-hub offer an assistant message carries, or `null` when it has none. */
export function toHubOffer(message: AssistantMessageLike): HubOffer | null {
  const call = (message.toolCalls || []).find(
    toolCall => toolCall.function?.name === AG_UI_TOOL.OFFER_HUB_ANSWER,
  );
  if (!call) return null;
  const args = parseToolArgs(call.function.arguments);
  return {
    offerId: call.id,
    variant: toVariant(args.variant),
    source: toSource(args.source),
    chunkIds: toChunkIds(args.chunk_ids),
  };
}
